import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Eye, RefreshCw } from "lucide-react";

const WINDOW_LABELS = [
  { key: "today", label: "Today" },
  { key: "last_7_days", label: "Last 7 days" },
  { key: "last_30_days", label: "Last 30 days" },
];

function StatCard({ label, views, uniques, testid }) {
  return (
    <div className="bg-[#111] border border-[#2A2A2A] p-6" data-testid={testid}>
      <p className="smallcaps mb-4">{label}</p>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-1 text-[#A3A3A3] text-xs uppercase tracking-widest">
            <Eye size={12} /> Views
          </div>
          <p className="font-display font-black text-4xl !text-white" data-testid={`${testid}-views`}>{views}</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1 text-[#A3A3A3] text-xs uppercase tracking-widest">
            <Users size={12} /> Visitors
          </div>
          <p className="font-display font-black text-4xl !text-white" data-testid={`${testid}-uniques`}>{uniques}</p>
        </div>
      </div>
    </div>
  );
}

function DailyChart({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.page_views));
  const fmt = (iso) => {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
  };
  return (
    <div className="bg-[#111] border border-[#2A2A2A] p-6" data-testid="analytics-chart">
      <div className="flex justify-between items-baseline mb-6">
        <p className="smallcaps">Daily page views — last 7 days</p>
        <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Views · Visitors</p>
      </div>
      <div className="flex items-end gap-2 sm:gap-4 h-56" role="img" aria-label="Daily page views bar chart">
        {daily.map((d) => {
          const pct = (d.page_views / max) * 100;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-2 group">
              <div className="text-[11px] font-display font-black !text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`bar-tip-${d.date}`}>
                {d.page_views}
              </div>
              <div className="w-full bg-[#1A1A1A] relative" style={{ height: "100%" }}>
                <div
                  className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-[#B87333] to-[#D89055] transition-all"
                  style={{ height: `${pct}%`, minHeight: d.page_views > 0 ? "2px" : 0 }}
                  data-testid={`bar-${d.date}`}
                />
              </div>
              <div className="text-[10px] text-[#A3A3A3] uppercase tracking-widest text-center leading-tight">
                {fmt(d.date)}
                <div className="text-[#555] font-mono normal-case tracking-normal">{d.page_views}·{d.unique_visitors}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/analytics")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !stats) return <p className="text-[#A3A3A3]">Loading analytics…</p>;
  if (!stats) return <p className="text-[#A3A3A3]">No data.</p>;

  return (
    <div data-testid="admin-analytics-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#F5F5DC] hidden lg:block">Site Analytics</h1>
          <p className="text-[#A3A3A3] text-sm mt-2 hidden md:block">Anonymous visitor counter — no third-party tracking, no personal data stored.</p>
        </div>
        <button onClick={load} className="text-[#A3A3A3] hover:text-[#B87333] p-2" aria-label="Refresh" data-testid="analytics-refresh">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {WINDOW_LABELS.map(({ key, label }) => (
          <StatCard
            key={key}
            label={label}
            views={stats[key].page_views}
            uniques={stats[key].unique_visitors}
            testid={`stat-${key}`}
          />
        ))}
      </div>

      <DailyChart daily={stats.daily_last_7_days} />

      {stats.top_paths_last_7_days.length > 0 && (
        <div className="bg-[#111] border border-[#2A2A2A] p-6" data-testid="top-paths">
          <p className="smallcaps mb-4">Top pages — last 7 days</p>
          <ul className="space-y-2">
            {stats.top_paths_last_7_days.map((p) => (
              <li key={p.path} className="flex justify-between items-baseline text-sm">
                <span className="text-[#F5F5DC] font-mono truncate mr-3">{p.path}</span>
                <span className="text-white font-black">{p.views} views</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
